import { Component, OnInit, ViewChild } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSlideToggleChange } from "@angular/material/slide-toggle";
import { MatStepper } from "@angular/material/stepper";
import { DomSanitizer } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject } from "rxjs";
import { mergeMap } from "rxjs/operators";
import { AuthService } from "src/app/core/service/auth.service";
import { SubscriptionService } from "src/app/core/service/subscription.service";
import { UserCustomFieldDTO } from "src/app/data/schema/Subscriptions/UserCustomFieldDTO";
import { UserCustomFieldTypeDTO } from "src/app/data/schema/Subscriptions/UserCustomFieldTypeDTO";
import { ApiCoursesService } from "src/app/data/service/courses/api-courses.service";
import { DataAnalysisService } from "src/app/data/service/data-analysis/data-analysis.service";
import { ApiUserService } from "src/app/data/service/user/api-user.service";
import { PrivacyPoliceDialogComponent } from "src/app/shared/component/dialogs/privacy-police-dialog/privacy-police-dialog.component";
import { TermsOfUseDialogComponent } from "src/app/shared/component/dialogs/terms-of-use-dialog/terms-of-use-dialog.component";
import { UserCustomFieldsComponent } from "src/app/shared/component/user-custom-fields/user-custom-fields.component";
import { environment } from "src/environments/environment";

@Component({
    selector: "app-registrar-checkout",
    templateUrl: "./registrar-checkout.component.html",
    styleUrls: ["./registrar-checkout.component.scss"]
})
export class RegistrarCheckoutComponent implements OnInit {
    public callbackUrl: string;

    nomeFormGroup: UntypedFormGroup;
    nascimentoFormGroup: UntypedFormGroup;
    emailFormGroup: UntypedFormGroup;
    redefinirSenhaForm: UntypedFormGroup;
    termosUsoFormGroup: UntypedFormGroup;
    politicaPrivacidadeFormGroup: UntypedFormGroup;

    termosUso: string;
    politicaPrivacidade: string;
    isLoading: boolean;
    error: string;
    errorApi: any;
    success: string;
    cadastrado: boolean;
    minDate: Date;
    maxDate: Date;
    uploadingFiles = false;

    _validPassword: boolean;
    _password: string;

    TermsOfUseClicked = false;
    privacyPolicyClicked = false;

    userId: string;

    userCustomFields$: BehaviorSubject<UserCustomFieldDTO[]>;
    get showUserCustomFieldsForm(): boolean {
        return this.userCustomFields$?.value.length > 0;
    }
    @ViewChild("appUserCustomFields") userCustomFieldsComponent: UserCustomFieldsComponent;

    constructor(
        private translate: TranslateService,
        private formBuilder: UntypedFormBuilder,
        private coursesService: ApiCoursesService,
        private userService: ApiUserService,
        private authService: AuthService,
        private router: Router,
        private subscriptionService: SubscriptionService,
        private domSanitizer: DomSanitizer,
        private activatedRoute: ActivatedRoute,
        public _matDialog: MatDialog,
        private dataAnalysisService: DataAnalysisService
    ) {
        this.isLoading = false;
        this.error = null;
        this.success = null;
        this.cadastrado = false;

        this.minDate = new Date(1900, 1, 1);
        this.maxDate = new Date(Date.now());
        this.userCustomFields$ = new BehaviorSubject<UserCustomFieldDTO[]>([]);
    }

    ngOnInit(): void {
        this.activatedRoute.queryParams.subscribe((params) => {
            this.callbackUrl = String(params.callbackUrl);
        });

        if (this.authService.getCurrentSubscriptionId() == null)
            this.subscriptionService.setCurrentSubscriptionId(environment.default_subscription_id);

        this.termosUso = this.subscriptionService.currentSubscription?.settings?.termsOfUseUrl;

        this.politicaPrivacidade = this.subscriptionService.currentSubscription?.settings?.privacyPolicyUrl;

        this.userCustomFields$.next(this.subscriptionService.currentSubscription?.settings?.userCustomFields);

        this.nomeFormGroup = this.formBuilder.group({
            nome: ["", Validators.required],
            sobrenome: ["", Validators.required]
        });

        this.emailFormGroup = this.formBuilder.group({
            email: ["", [Validators.required, Validators.email]]
        });

        this.termosUsoFormGroup = this.formBuilder.group({
            aceitoTermos: ["", Validators.required]
        });

        this.politicaPrivacidadeFormGroup = this.formBuilder.group({
            concordoPoliticaPrivacidade: ["", Validators.required]
        });
    }

    onChange($event: MatSlideToggleChange, stepper: MatStepper): void {
        if ($event.checked) {
            stepper.next();
        }
    }

    login(): void {
        this.isLoading = true;

        if (!this._validPassword) return;

        this.authService
            .loginPassword(this.emailFormGroup.value.email, this._password, this.authService.getCurrentSubscriptionId())
            .subscribe(
                () => {
                    this.router.navigateByUrl(this.callbackUrl);
                    this.isLoading = false;
                },
                () => {
                    this.translate
                        .get("REGISTER.DONE.MESSAGES.AUTH_GENERIC_ERROR")
                        .subscribe((res) => (this.error = res));
                    this.isLoading = false;
                }
            );
    }

    cadastrarUsuario(): void {
        this.isLoading = true;
        this.error = null;
        this.success = null;
        this.errorApi = null;

        if (!this._validPassword) return;

        const filefieldName = this.userCustomFields$.value
            .filter((x) => x.type == UserCustomFieldTypeDTO.File)
            ?.map((x) => x.fieldName);

        this.userService
            .create(
                this.nomeFormGroup.value.nome,
                this.nomeFormGroup.value.sobrenome,
                this.emailFormGroup.value.email,
                [],
                [],
                this._password,
                null,
                null,
                false,
                this.userCustomFieldsComponent?.exportValues() || null
            )
            .pipe(
                mergeMap((user) => {
                    this.userId = user.id;

                    return this.authService.loginPassword(
                        this.emailFormGroup.value.email,
                        this._password,
                        this.authService.getCurrentSubscriptionId()
                    );
                })
            )
            .subscribe(
                () => {
                    filefieldName?.forEach((field) => {
                        const file = this.userCustomFieldsComponent?.filesToUpload[field];
                        const last = filefieldName.indexOf(field) == filefieldName.length - 1;
                        if (file) {
                            this.uploadingFiles = true;
                            this.uploadFileCustomField(this.userId, file, field, last);
                        }
                    });

                    this.cadastrado = true;
                    this.translate.get("REGISTER.DONE.MESSAGES.CREATED").subscribe((res) => (this.success = res));
                    this.isLoading = false;
                },
                (error) => {
                    this.translate
                        .get("REGISTER.DONE.MESSAGES.CREATE_GENERIC_ERROR")
                        .subscribe((res) => (this.error = res));
                    if (error.status >= 400 && error.status <= 499) this.errorApi = error.error.errors;
                    this.isLoading = false;
                }
            );
    }

    validPassword($event: boolean): void {
        this._validPassword = $event;
    }

    password(event: string): void {
        this._password = event;
    }

    openTermsOfUseDialog(): void {
        this.TermsOfUseClicked = true;
        this._matDialog.open(TermsOfUseDialogComponent);
    }

    openPrivacyPoliceDialog(): void {
        this.privacyPolicyClicked = true;
        this._matDialog.open(PrivacyPoliceDialogComponent);
    }

    trimEmail(): void {
        this.emailFormGroup.controls.email.setValue(this.emailFormGroup.controls.email.value.trim());
    }

    public uploadFileCustomField(userId: string, input: HTMLInputElement, fieldKey: string, last: boolean): void {
        if (input.files.length > 0)
            this.userService.uploadFileCustomField(userId, input.files[0], fieldKey).subscribe(
                () => {
                    if (last) this.uploadingFiles = false;
                },
                () => {
                    if (last) this.uploadingFiles = false;
                }
            );
    }

    getPasswordRegex(): string {
        return this.subscriptionService.currentSubscription?.loginSettings?.defaultLoginOptions?.passwordRegex;
    }

    getPasswordCustomRules(): string {
        return this.subscriptionService.currentSubscription?.loginSettings?.defaultLoginOptions?.passwordCustomRules;
    }
}
